import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button, TextInput, Typography } from "@/components/ui";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import useSidebarStore from "@/store/useSidebarStore";

type JobNameSchema = {
  job_name?: string | undefined;
};

interface JobHeaderProps {
  isPositionFixed: boolean;
  register: UseFormRegister<JobNameSchema>;
  errors: FieldErrors<JobNameSchema>;
  atLeastOneSectionFilled: boolean;
  areAllRequiredFieldsFilled: boolean;
  isPendingCreateJob: boolean;
  isPendingPreview: boolean;
  onSaveAsDraft: () => void;
  onRunCalculations: () => void;
  canRunCalculations?: boolean;
}

export function JobHeader({
  isPositionFixed,
  register,
  errors,
  atLeastOneSectionFilled,
  areAllRequiredFieldsFilled,
  isPendingCreateJob,
  isPendingPreview,
  onSaveAsDraft,
  onRunCalculations,
  canRunCalculations = true,
}: JobHeaderProps) {
  const isTabletOrAbove = useScreens("md", "up");
  const navigate = useNavigate();
  const { isOpen: isSidebarOpen } = useSidebarStore();

  return (
    <div
      className={cn(
        "fixed z-30 flex flex-col items-center border-b border-solid border-b-black bg-white pb-4 md:flex-row",
        isPositionFixed && isTabletOrAbove ? "fixed top-0  pt-4" : "static",
        isSidebarOpen && isPositionFixed && isTabletOrAbove && "w-[calc(100vw-350px)]",
        !isSidebarOpen && isPositionFixed && isTabletOrAbove && "w-[calc(100vw-140px)]"
      )}
    >
      <div className={cn(!isTabletOrAbove && "relative mb-4 flex w-full flex-1")}>
        {!isTabletOrAbove && (
          <button className="left-1 cursor-pointer" onClick={() => navigate("/jobs")}>
            <Typography className="text-primary-500" variant="text-md" weight="medium">
              Back
            </Typography>
          </button>
        )}
        <Typography
          variant={isTabletOrAbove ? "display-lg" : "text-md"}
          weight="semibold"
          className={cn(!isTabletOrAbove && "ml-20")}
        >
          Create new job
        </Typography>
      </div>
      <TextInput
        className="mb-8 mr-4 w-full md:mb-0 md:ml-auto md:w-fit"
        label="Plant Repair Identifier"
        placeholder="Plant Repair Identifier"
        {...register("job_name")}
        error={errors.job_name?.message}
      />
      {isTabletOrAbove && (
        <div className="max-w-[400px] self-end">
          <div className="flex flex-wrap gap-4">
            <Button
              disabled={!atLeastOneSectionFilled || !!errors.job_name?.message || isPendingCreateJob}
              onClick={onSaveAsDraft}
              className="self-end"
              size="lg"
            >
              Save as draft
            </Button>
            {canRunCalculations && (
              <div>
                <Button
                  disabled={!areAllRequiredFieldsFilled || !!errors.job_name?.message || isPendingPreview}
                  onClick={onRunCalculations}
                  size="lg"
                >
                  Run calculations
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
